/**
 * MyRequests — the trip requests the signed-in customer has submitted.
 *
 * Route: /user/requests
 *
 * Complements the trip-builder exit: after a user requests a finalized plan,
 * this is where they track every request and its status. Read-only (the team
 * moves requests through the pipeline from the admin side).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Stack,
  Grid,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Send,
  Groups,
  Event,
  CalendarMonth,
  ArrowForward,
  EditNote,
} from '@mui/icons-material';
import plansService from '../../services/plansService';
import ButtonPill from '../ui/ButtonPill';
import BookPlanDialog from './BookPlanDialog';

const STATUS_COLORS = {
  pending: 'warning',
  contacted: 'info',
  confirmed: 'success',
  closed: 'default',
};

const STATUS_HINT = {
  pending: 'We have your request and will be in touch soon.',
  contacted: 'Our team has reached out about this trip.',
  confirmed: 'Approved. Our team will be in touch to finalize your booking.',
  closed: 'This request has been closed.',
};

// Customers see "confirmed" as the friendlier "approved".
const STATUS_LABEL = { confirmed: 'approved' };

const formatCurrency = (value, currency = 'USD') => {
  if (value == null || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

const MyRequests = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookReq, setBookReq] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await plansService.getMyRequests();
      setRequests(res?.requests ?? []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 1 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        My Trip Requests
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Track the trips you've asked us to arrange.
      </Typography>

      {requests.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}`, textAlign: 'center' }}
        >
          <Send sx={{ fontSize: 56, color: theme.palette.text.disabled, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            No requests yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
            Build a trip, finalize it, and hit "Request This Trip". It will show up here so you can
            track its progress.
          </Typography>
          <ButtonPill variant="contained" startIcon={<EditNote />} onClick={() => navigate('/user/plans')}>
            Go to My Plans
          </ButtonPill>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {requests.map((req) => {
            const total = formatCurrency(req.plan?.calculated_total, req.plan?.currency);
            const preferred = formatDate(req.preferred_date);
            const submitted = formatDate(req.created_at);
            return (
              <Paper
                key={req.id}
                elevation={0}
                sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {req.plan?.title || 'Untitled plan'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Requested {submitted}
                    </Typography>
                  </Box>
                  <Chip
                    label={STATUS_LABEL[req.status] || req.status}
                    color={STATUS_COLORS[req.status] || 'default'}
                    sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                  />
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  {STATUS_HINT[req.status] || ''}
                </Box>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Groups sx={{ fontSize: 15 }} /> Travelers
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>{req.num_travelers ?? '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Event sx={{ fontSize: 15 }} /> Preferred date
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>{preferred || '—'}</Typography>
                  </Grid>
                  {total && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarMonth sx={{ fontSize: 15 }} /> Estimated total
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>{total}</Typography>
                    </Grid>
                  )}
                </Grid>

                {req.plan?.id && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
                    <ButtonPill
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate(`/builder/${req.plan.id}`)}
                    >
                      View itinerary
                    </ButtonPill>
                    {req.status === 'confirmed' && (
                      <ButtonPill
                        variant="contained"
                        size="small"
                        startIcon={<Send />}
                        onClick={() => setBookReq(req)}
                      >
                        Book now
                      </ButtonPill>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <BookPlanDialog
        open={!!bookReq}
        onClose={() => setBookReq(null)}
        planId={bookReq?.plan?.id}
        defaultTravelers={bookReq?.num_travelers}
        defaultTravelDate={bookReq?.preferred_date}
        onBooked={load}
      />
    </Container>
  );
};

export default MyRequests;
